import { useCallback, useEffect, useState } from 'react';
import { formatDateTime } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import type { DesempenhoDoModelo } from '../comparar';
import { avaliarRisco, estimarEnvio, tarifaPorMensagemCents } from '../estimativa';
import { LIMITE_AUTOPAUSA_BPS, formularioInicial, pendenciasDoFormulario, publicoDoFormulario, type FormularioEnvio } from '../formulario';
import type { Broadcast, MessageTemplateSummary, NumberHealth, SegmentFamily, SegmentKey, SenderOption, WhatsAppContext } from '../types';
import { useFeedbackTemporario } from '../useFeedbackTemporario';
import { FormularioNovoEnvio } from './FormularioNovoEnvio';
import { ModalAgendar } from './ModalAgendar';
import { ModalEnviarTeste } from './ModalEnviarTeste';
import { ModalEscolherModelo } from './ModalEscolherModelo';
import { PreviaDoEnvio } from './PreviaDoEnvio';
import { ProtecaoDoNumero } from './ProtecaoDoNumero';

/**
 * Novo envio em massa: formulario e protecao do numero a esquerda; previa,
 * estimativa e botoes a direita.
 *
 * Decisoes nao obvias:
 *  1. O estado do FORMULARIO mora aqui e e descartado quando a empresa muda
 *     (a pagina monta esta aba com `key={company.id}`): um publico marcado
 *     para a Escola Fluente nao pode sobreviver a troca para a FitPro.
 *  2. `preparo` e o atalho "Preparar envio" (do hub, do radar ou do "O que
 *     fazer agora"): marca o publico sugerido sem apagar o resto do que ja
 *     foi preenchido, e rola ate o formulario.
 *  3. Estimativa e risco sao recalculados a cada tecla a partir de regras
 *     puras (estimativa.ts): os numeros da direita nunca discordam do
 *     formulario da esquerda.
 *  4. Iniciar e agendar criam um `Broadcast` com os contadores zerados; o
 *     progresso quem faz e `useProgressoSimulado`, na pagina.
 */

export interface PreparoDeEnvio {
  readonly versao: number;
  readonly segmento: SegmentKey;
}

interface Props {
  readonly company: CompanySummary;
  readonly contexto: WhatsAppContext;
  readonly templates: readonly MessageTemplateSummary[];
  readonly sender: SenderOption | null;
  readonly health: NumberHealth | null;
  readonly desempenho: ReadonlyMap<string, DesempenhoDoModelo>;
  readonly preparo: PreparoDeEnvio | null;
  readonly onCriar: (envio: Broadcast) => void;
  readonly proximoNumeroLocal: number;
}

export function AbaCampanhas({ company, contexto, templates, sender, health, desempenho, preparo, onCriar, proximoNumeroLocal }: Props): JSX.Element {
  const [form, setForm] = useState<FormularioEnvio>(() => formularioInicial(preparo?.segmento ?? null));
  const alterar = useCallback((patch: Partial<FormularioEnvio>): void => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const versaoDoPreparo = preparo?.versao ?? 0;
  const segmentoDoPreparo = preparo?.segmento ?? null;
  useEffect(() => {
    if (segmentoDoPreparo === null) {
      return;
    }
    setForm((prev) => ({ ...prev, fonte: 'segmentos', segmentos: prev.segmentos.includes(segmentoDoPreparo) ? prev.segmentos : [segmentoDoPreparo] }));
    document.getElementById('novo-envio')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [versaoDoPreparo, segmentoDoPreparo]);

  const [modalModelo, setModalModelo] = useState(false);
  const [modalTeste, setModalTeste] = useState(false);
  const [modalAgendar, setModalAgendar] = useState(false);
  const { mensagem, mostrar } = useFeedbackTemporario();

  const template = templates.find((t) => t.id === form.templateId) ?? null;
  const publico = publicoDoFormulario(form, contexto.segments);
  const escolhidos = form.fonte === 'segmentos' ? contexto.segments.filter((s) => form.segmentos.includes(s.key)) : [];
  const familias: readonly SegmentFamily[] = Array.from(new Set(escolhidos.map((s) => s.family)));

  const estimativa = estimarEnvio({
    contatos: publico.contatos,
    segmentos: escolhidos,
    senderKind: sender?.kind ?? null,
    category: template?.category ?? null,
    tariffs: contexto.tariffs,
    feeBps: company.feeBps,
    porMinuto: form.porMinuto,
    janelaInicio: form.janelaInicio,
    janelaFim: form.janelaFim,
    dailyLimit: health?.dailyLimit ?? null,
  });

  const risco = avaliarRisco({
    senderKind: sender?.kind ?? null,
    health,
    porMinuto: form.porMinuto,
    janelaInicio: form.janelaInicio,
    janelaFim: form.janelaFim,
    contatos: publico.contatos,
    category: template?.category ?? null,
  });

  const remetenteOk = sender !== null && sender.connected;
  const modeloTravado = template !== null && sender !== null && sender.kind === 'oficial' && template.approval !== 'aprovado';
  const pendencias = [
    ...pendenciasDoFormulario(form, publico.contatos),
    ...(remetenteOk ? [] : ['conecte um número de saída']),
    ...(modeloTravado ? ['troque o modelo: ele ainda não foi aprovado para a API oficial'] : []),
  ];

  const criarEnvio = (status: 'em_andamento' | 'agendada', scheduledFor: string | null): void => {
    // Mesma regra dos botoes: nada sai com pendencia (defesa contra chamada fora do botao).
    if (template === null || sender === null || pendencias.length > 0) {
      return;
    }
    const agora = new Date().toISOString();
    const novo: Broadcast = {
      id: `SIMULADO-envio-local-${String(proximoNumeroLocal).padStart(3, '0')}`,
      companyId: company.id,
      name: form.nome.trim(),
      senderKind: sender.kind,
      templateId: template.id,
      templateName: template.name,
      templateCategory: template.category,
      status,
      pausedReason: null,
      audience:
        form.fonte === 'csv'
          ? { source: 'csv', segments: [], fileName: form.csv === null ? null : form.csv.fileName }
          : { source: 'segmentos', segments: form.segmentos, fileName: null },
      createdAt: agora,
      scheduledFor,
      startedAt: status === 'em_andamento' ? agora : null,
      finishedAt: null,
      totalContacts: publico.contatos,
      sentCount: 0,
      deliveredCount: 0,
      readCount: 0,
      clickedCount: 0,
      repliedCount: 0,
      failedCount: 0,
      optOutCount: 0,
      paidCount: 0,
      paidViaSellerCount: 0,
      recoveredAmountCents: 0,
      commissionCents: 0,
      costPerMessageCents: tarifaPorMensagemCents(sender.kind, template.category, contexto.tariffs),
      assignedSellerIds: form.vendedoras,
      perMinute: form.porMinuto,
      autoPauseOptOutBps: form.pausarSeOptOutAlto ? LIMITE_AUTOPAUSA_BPS : null,
    };
    onCriar(novo);
    // Mantem o que e reaproveitavel (modelo, ritmo, janela, atribuicao, travas) e zera o que e do envio.
    setForm((prev) => ({
      ...formularioInicial(null),
      templateId: prev.templateId,
      vendedoras: prev.vendedoras,
      porMinuto: prev.porMinuto,
      janelaInicio: prev.janelaInicio,
      janelaFim: prev.janelaFim,
      avancadoAberto: prev.avancadoAberto,
      pularRecentes: prev.pularRecentes,
      pausarSeOptOutAlto: prev.pausarSeOptOutAlto,
    }));
    mostrar(
      status === 'agendada'
        ? `"${novo.name}" agendado para ${formatDateTime(scheduledFor)}. Está na lista de envios.`
        : `"${novo.name}" iniciado. Acompanhe o progresso na lista de envios; os pagamentos aparecem em até 7 dias.`,
    );
  };

  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <div className="flex min-w-0 flex-col gap-6 lg:col-span-3">
        <FormularioNovoEnvio
          form={form}
          onChange={alterar}
          template={template}
          segments={contexto.segments}
          sellers={contexto.sellers}
          company={company}
          sender={sender}
          onEscolherModelo={() => setModalModelo(true)}
        />
        <ProtecaoDoNumero risco={risco} sender={sender} health={health} travaLigada={form.pausarSeOptOutAlto} />
      </div>
      <PreviaDoEnvio
        className="min-w-0 lg:col-span-2"
        companyName={company.name}
        template={template}
        sender={sender}
        sample={contexto.sample}
        estimativa={estimativa}
        publico={publico}
        tariffs={contexto.tariffs}
        feeBps={company.feeBps}
        sellers={contexto.sellers}
        form={form}
        pendencias={pendencias}
        onChange={alterar}
        onEnviarTeste={() => setModalTeste(true)}
        onAgendar={() => setModalAgendar(true)}
        onIniciar={() => criarEnvio('em_andamento', null)}
        feedback={mensagem}
      />

      <ModalEscolherModelo
        open={modalModelo}
        onClose={() => setModalModelo(false)}
        templates={templates}
        selectedId={form.templateId}
        onSelect={(t) => alterar({ templateId: t.id })}
        senderKind={sender?.kind ?? null}
        familias={familias}
        desempenho={desempenho}
      />
      <ModalEnviarTeste open={modalTeste} onClose={() => setModalTeste(false)} template={template} companyName={company.name} />
      <ModalAgendar
        open={modalAgendar}
        onClose={() => setModalAgendar(false)}
        janelaInicio={form.janelaInicio}
        janelaFim={form.janelaFim}
        onConfirm={(iso) => {
          setModalAgendar(false);
          criarEnvio('agendada', iso);
        }}
      />
    </div>
  );
}
